import { useEffect, useMemo } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import CompactToggle from '~/components/toolbar/shared/CompactToggle';
import ToolEntryButton from '~/components/toolbar/shared/ToolEntryButton';
import ToolPropertiesTray from '~/components/toolbar/shared/ToolPropertiesTray';
import { ToolRailPortal } from '~/components/toolbar/shared/ToolRailSlots';
import { useAcquiredPrimarySliceShown } from '~/hooks/environment/useEnvironmentViews';
import { useActiveEditTarget } from '~/hooks/target/useActiveEditTarget';
import { useStructureToolGating } from '~/hooks/target/useStructureToolGating';
import { RoiInterpretedType } from '~/domain/dicom/dicomstructureset';
import { findRoisByInterpretedType } from '~/domain/readmodels/StructureSetReadModel';
import { useContourAutoSuggestionController } from '~/hooks/useContourAutoSuggestionController';

import {
    brushOptionsState,
    CONTOUR_STROKE_TOOLS,
    isMoveTool,
    lineOptionsState,
    selectedToolState,
    contourAutoSuggestionEnabledState,
    Tools,
    writesStructure,
} from '~/state/atoms/toolsAtoms';
import DrawingConstraintsControl from './modifiers/DrawingConstraintsControl';
import BrushToolOptions from './BrushToolOptions';
import DeformToolOptions from './DeformToolOptions';
import LineDeformToolOptions from './LineDeformToolOptions';
import LineToolOptions from './LineToolOptions';
import EraserToolOptions from './EraserToolOptions';
import SmoothBrushToolOptions from './SmoothBrushToolOptions';
import CrossSectionApplyButton from './crossSection/CrossSectionApplyButton';
import CrossSectionPreviewHost from './crossSection/CrossSectionPreviewHost';
import CrossSectionToolOptions from './crossSection/CrossSectionToolOptions';
import TransformToolOptions from './TransformToolOptions';
import { useContouringToolEntries } from './useContouringToolEntries';
import ToolOptionsSummary from './ToolOptionsSummary';

/** Content width of the Cross-Section hint, in CSS pixels. */
const CROSS_SECTION_HINT_MAX_WIDTH_PX = 224;

/**
 * The tool to arm in place of `tool` for the selection, when `tool` writes
 * to a structure: Pan while nothing can be edited; on a point ROI, the
 * point move for a move and Pan for any other write tool; on any other
 * ROI, the structure move for a point move. A tool that writes nothing
 * stays.
 */
function writeToolForSelection(
    tool: Tools,
    editBlocked: boolean,
    isPointRoi: boolean
): Tools {
    if (!writesStructure(tool)) {
        return tool;
    }
    if (editBlocked) {
        return 'pan';
    }
    if (isPointRoi) {
        return isMoveTool(tool) ? 'poi-move' : 'pan';
    }
    return tool === 'poi-move' ? 'transform' : tool;
}

export default function ContouringTools() {
    const [tool, setTool] = useRecoilState(selectedToolState);
    const {
        selectedStructureSet,
        selectedRoiNumber,
        selectedRoi,
        isPointRoi,
        selectionEditBlocker,
        isBulkSelectionActive,
        selectedRoiAnyPlaneEditDisabled,
        targetUnresolved,
        isPrimaryImageIncomplete,
    } = useStructureToolGating();
    const brushOptions = useRecoilValue(brushOptionsState);
    const lineOptions = useRecoilValue(lineOptionsState);
    const [autoSuggestEnabled, setAutoSuggestEnabled] = useRecoilState(
        contourAutoSuggestionEnabledState
    );
    const { environment } = useActiveEditTarget();
    const autoSuggestAvailable = useAcquiredPrimarySliceShown(environment);
    const entries = useContouringToolEntries();

    useContourAutoSuggestionController();

    const isAnyPlaneToolsDisabled = selectedRoiAnyPlaneEditDisabled;

    const isEraseSelected = entries.erase.selected;

    const editBlocked =
        selectedRoiNumber === -1 ||
        selectionEditBlocker !== undefined ||
        isBulkSelectionActive ||
        // With no editable target every tool returns to pan, as for the
        // other terms; a button left lit would claim a tool that is not
        // armed.
        targetUnresolved ||
        // Switching the primary image to one that is still streaming must
        // disarm an already-armed mutating tool, not just grey its button.
        isPrimaryImageIncomplete;

    // A selection that cannot be edited, or a point ROI, returns a viewing
    // tool to pan too, as each new selection is made; a move on a point ROI
    // stays for the rule below. Every write tool answers to
    // writeToolForSelection.
    useEffect(() => {
        if (editBlocked || isPointRoi) {
            setTool((prev) =>
                !editBlocked && isMoveTool(prev) ? prev : 'pan'
            );
        }
    }, [editBlocked, isPointRoi, selectedRoiNumber, setTool]);

    // A write tool selected later, such as one an operation puts back as it
    // closes; a viewing tool stays.
    useEffect(() => {
        const next = writeToolForSelection(tool, editBlocked, isPointRoi);
        if (next !== tool) {
            setTool(next);
        }
    }, [tool, editBlocked, isPointRoi, setTool]);

    useEffect(() => {
        // The auto-suggest toggle persists across tool switches —
        // turning it on while in brush mode keeps it on when the
        // user switches to line / line-deform / etc. so they don't
        // have to re-enable the feature each time. We only force it
        // off when contouring as a whole is unavailable for the
        // current selection (no ROI, point ROI, approved structure, or a
        // structure set on no grid row), since the controller has nothing
        // to operate on in those states. Whether the displayed plane can
        // carry a suggestion is a property of the view, not the selection,
        // and is stated on the switch itself.
        if (isAnyPlaneToolsDisabled) {
            setAutoSuggestEnabled(false);
        }
    }, [isAnyPlaneToolsDisabled, setAutoSuggestEnabled]);

    const isSelectedSupport =
        selectedRoi?.interpretedType === RoiInterpretedType.SUPPORT;
    const hasSiblingSupport = useMemo(() => {
        if (!isSelectedSupport || !selectedStructureSet) {
            return false;
        }
        return (
            findRoisByInterpretedType(
                selectedStructureSet.roiArray,
                RoiInterpretedType.SUPPORT,
                selectedRoiNumber
            ).length > 0
        );
    }, [isSelectedSupport, selectedStructureSet, selectedRoiNumber]);

    // Erase spans two tools — brush erase and line erase. Neither applies the
    // Inside/Outside constraints (the draw action short-circuits them for erase
    // strokes), so the popover annotates the sections as dormant in both.
    const isErasing =
        isEraseSelected || (tool === 'line' && lineOptions.mode === 'erase');

    const constraintsSection = (
        <DrawingConstraintsControl
            disabled={isAnyPlaneToolsDisabled}
            erasing={isErasing}
        />
    );

    const autoSuggestOperable =
        autoSuggestAvailable && !isAnyPlaneToolsDisabled;
    const autoSuggestToggle = CONTOUR_STROKE_TOOLS.includes(tool) ? (
        <CompactToggle
            labelId="sliceDeformSuggest"
            tooltipId={
                autoSuggestAvailable
                    ? 'sliceDeformSuggestTooltip'
                    : 'sliceDeformSuggestNeedsTransversalSlice'
            }
            // Reads off while unavailable; the setting itself is kept.
            value={autoSuggestEnabled && autoSuggestAvailable}
            onChange={(next) => {
                // A scripted click can still change a disabled checkbox.
                if (autoSuggestOperable) {
                    setAutoSuggestEnabled(next);
                }
            }}
            disabled={!autoSuggestOperable}
            dataTestId="tool-auto-suggest"
        />
    ) : null;
    const brushEntry =
        brushOptions.mode === 'smart'
            ? entries.adaptiveBrush
            : entries.staticBrush;

    return (
        <>
            <ToolRailPortal group="tools">
                {Object.values(entries).map((entry) => (
                    <ToolEntryButton
                        key={entry.id}
                        entry={entry}
                        hintMaxWidthPx={
                            entry.id === 'cross-section'
                                ? CROSS_SECTION_HINT_MAX_WIDTH_PX
                                : undefined
                        }
                    />
                ))}
            </ToolRailPortal>
            {tool === 'brush' && !brushOptions.erase && (
                <ToolPropertiesTray
                    title={brushEntry.label}
                    icon={brushEntry.icon}
                    summary={<ToolOptionsSummary tool="brush" showsModifiers />}
                    footer={constraintsSection}
                >
                    <BrushToolOptions>{autoSuggestToggle}</BrushToolOptions>
                </ToolPropertiesTray>
            )}
            {isEraseSelected && (
                <ToolPropertiesTray
                    title={entries.erase.label}
                    icon={entries.erase.icon}
                    // A brush erase or a rectangle erase.
                    summary={<ToolOptionsSummary tool={tool} />}
                >
                    <EraserToolOptions>{autoSuggestToggle}</EraserToolOptions>
                </ToolPropertiesTray>
            )}
            {tool === 'line' && (
                <ToolPropertiesTray
                    title={entries.lineDraw.label}
                    icon={entries.lineDraw.icon}
                    // An erase stroke takes no modifier.
                    summary={
                        <ToolOptionsSummary
                            tool="line"
                            showsModifiers={!isErasing}
                        />
                    }
                    footer={constraintsSection}
                >
                    <LineToolOptions>{autoSuggestToggle}</LineToolOptions>
                </ToolPropertiesTray>
            )}
            {tool === 'lineDeform' && (
                <ToolPropertiesTray
                    title={entries.lineDeform.label}
                    icon={entries.lineDeform.icon}
                    summary={<ToolOptionsSummary tool="lineDeform" />}
                >
                    <LineDeformToolOptions>
                        {autoSuggestToggle}
                    </LineDeformToolOptions>
                </ToolPropertiesTray>
            )}
            {tool === 'deform' && (
                <ToolPropertiesTray
                    title={entries.deform.label}
                    icon={entries.deform.icon}
                    summary={<ToolOptionsSummary tool="deform" />}
                >
                    <DeformToolOptions>{autoSuggestToggle}</DeformToolOptions>
                </ToolPropertiesTray>
            )}
            {tool === 'smoothBrush' && (
                <ToolPropertiesTray
                    title={entries.smoothBrush.label}
                    icon={entries.smoothBrush.icon}
                    summary={<ToolOptionsSummary tool="smoothBrush" />}
                >
                    <SmoothBrushToolOptions>
                        {autoSuggestToggle}
                    </SmoothBrushToolOptions>
                </ToolPropertiesTray>
            )}
            {tool === 'transform' && isSelectedSupport && hasSiblingSupport && (
                <ToolPropertiesTray
                    title={entries.transform.label}
                    icon={entries.transform.icon}
                >
                    <TransformToolOptions />
                </ToolPropertiesTray>
            )}
            {tool === 'cross-section' && (
                <>
                    <CrossSectionPreviewHost />
                    <ToolPropertiesTray
                        title={entries.crossSection.label}
                        icon={entries.crossSection.icon}
                        summary={
                            <ToolOptionsSummary
                                tool="cross-section"
                                showsModifiers
                            />
                        }
                        // Apply follows the contours drawn on the views.
                        collapsesOnViewPress={false}
                        footer={
                            <>
                                {constraintsSection}
                                <CrossSectionApplyButton />
                            </>
                        }
                    >
                        <CrossSectionToolOptions />
                    </ToolPropertiesTray>
                </>
            )}
        </>
    );
}
